import { isSameFieldDatatype } from '@framework/engine/schema/expressions/datatype/Datatype.ts';
import { ExpressionReferenceInput } from '@framework/engine/schema/expressions/references/input/ExpressionReferenceInput.tsx';
import { ReferenceSchema } from '@framework/engine/schema/expressions/validation/ReferenceSchema.ts';
import { type IWidgetComponentProps } from '@framework/engine/schema/widgets/IWidgetType.ts';
import { BaseLayoutSchema } from '@framework/engine/schema/widgets/repository/common/BaseLayoutSchema.ts';
import { LayoutHideConfigInput } from '@framework/engine/schema/widgets/repository/common/config/LayoutHideConfigInput.tsx';
import {
  CommonConfigComponent,
  CommonInputWidgetConfigSchema,
} from '@framework/engine/schema/widgets/repository/inputs/common/CommonInputConfigComponent.tsx';
import { WidgetRepository } from '@framework/engine/schema/widgets/repository/WidgetRepository.ts';
import { useTreeReferencesContext } from '@framework/engine/tree/references/TreeReferencesContext.tsx';
import { SwitchInput } from '@framework/form/inputs/SwitchInput.tsx';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { RowElement } from '@framework/ui/components/row/RowElement.tsx';
import { yup } from '@framework/validation/initYup.ts';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { SecondaryCompetenceTypeAsyncSelect } from '../../../schema/secondary-competence-types/SecondaryCompetenceTypeAsyncSelect.tsx';
import {
  type ICompetenceWidget,
  WidgetTypeCompetence,
} from './ICompetenceWidget.ts';

export const CompetenceWidgetConfigSchema: yup.ObjectSchema<ICompetenceWidget> =
  CommonInputWidgetConfigSchema.shape({
    type: yup.string().exact(WidgetTypeCompetence).required(),
    layout: BaseLayoutSchema.shape({
      hide: yup.boolean().required().default(false).label('Nascondi elemento'),
    }),
    sourceValueReference: ReferenceSchema.notRequired()
      .label('Assegna automaticamente')
      .when('layout.hide', ([hide]: boolean[], schema) => {
        return hide ? schema : schema.strip();
      }),
    isExtraCompetence: yup
      .boolean()
      .default(false)
      .label('Competenza secondaria'),
    secondaryCompetenceTypeId: yup
      .string()
      .label('Tipo competenza secondaria')
      .when(['isExtraCompetence'], ([isExtraCompetence], schema) =>
        isExtraCompetence ? schema.required() : schema.strip(),
      ),
  })
    .required()
    .noUnknown();

export interface ICompetenceInputConfigComponentProps
  extends IWidgetComponentProps<ICompetenceWidget> {}

export function CompetenceInputConfigComponent(
  props: ICompetenceInputConfigComponentProps,
) {
  const isHidden = useWatch({ name: 'layout.hide', exact: true });
  const isExtraCompetence = useWatch({
    name: 'isExtraCompetence',
    exact: true,
  });
  const { references, widget } = useTreeReferencesContext();

  const fieldDataType = useMemo(
    () =>
      WidgetRepository.find(widget?.type)?.getReference?.([], widget!)?.field,
    [widget],
  );

  const expressionReferences = useMemo(() => {
    if (!isHidden) return [];
    return references.filter(
      r =>
        // Deve avere lo stesso fieldDataType e non essere il campo stesso
        isSameFieldDatatype(r.field, fieldDataType) && r.id !== widget?.id,
    );
  }, [isHidden, references, fieldDataType, widget?.id]);

  return (
    <div>
      <CommonConfigComponent {...props} />
      <RowElement>
        <FormItem
          name="isExtraCompetence"
          label=""
          help="La competenza secondaria non sarà considerata come competenza principale. Una richiesta dovrebbe avere una sola competenza principale."
        >
          <SwitchInput name="isExtraCompetence" defaultChecked={false}>
            Competenza secondaria
          </SwitchInput>
        </FormItem>
      </RowElement>
      {isExtraCompetence && (
        <RowElement>
          <FormItem
            name="secondaryCompetenceTypeId"
            label="Tipo competenza secondaria"
            help="Tipo a cui associare la competenza secondaria selezionata."
          >
            <SecondaryCompetenceTypeAsyncSelect
              name="secondaryCompetenceTypeId"
              allowClear
              placeholder="Seleziona tipo competenza secondaria"
            />
          </FormItem>
        </RowElement>
      )}
      <LayoutHideConfigInput />
      {/* Se il campo competenza è nascosto posso richiedere che il suo valore si riferisca a un altro campo */}
      {isHidden && (
        <RowElement>
          <FormItem
            name="sourceValueReference"
            label="Assegna automaticamente"
            help="Se il campo viene nascosto, è possibile impostare il suo valore a quello di un altro campo indicato."
          >
            <ExpressionReferenceInput
              size="large"
              key="reference"
              name="sourceValueReference"
              width="100%"
              references={expressionReferences}
            />
          </FormItem>
        </RowElement>
      )}
    </div>
  );
}
